import { useEffect, useRef } from 'react'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'

interface Options {
  nodes: OperationsNode[]
  isLoading: boolean
  onInitialise: (expandIds: string[], rootId: string) => void
}

/** Every id in the subtree — shared with the search-expand behaviour. */
export const collectIds = (nodes: OperationsNode[]): string[] =>
  nodes.flatMap((node) => [node.id, ...collectIds(node.children)])

/**
 * Sets the tree's one-shot initial state the first time it arrives: every
 * node expanded, root season selected. Fires at most once, the same
 * "has-fired" ref pattern as `useOpenAddTeamDeepLink` — the tree query
 * invalidates after every mutation, and re-running this on each of those
 * refetches would yank the user's own expand/collapse and selection state
 * out from under them.
 */
export const useInitialTreeState = ({
  nodes,
  isLoading,
  onInitialise,
}: Options): void => {
  const hasInitialised = useRef(false)

  useEffect(() => {
    if (hasInitialised.current || isLoading || nodes.length === 0) {
      return
    }

    hasInitialised.current = true
    onInitialise(collectIds(nodes), nodes[0].id)
  }, [nodes, isLoading, onInitialise])
}
